<template>
  <v-app>
    <div class="error-page-wrapper" :style="pageBackground">
      <!-- Header components con carga segura -->
      <StickyBar v-if="hasNotaria && NOTARIA.Theme?.DatosContacto?.MostrarStickyBar" />
      <HeaderNotinOne v-if="hasNotaria" />

      <!-- Main Content -->
      <v-main class="error-main-content d-flex align-center justify-center">
        <v-container>
          <v-row align="center" justify="center">
            <v-col cols="12" class="text-center">
              <div class="error-glass-container">
                <div class="inner">
                  <h1 class="error-code-identity" :style="codeStyle">404</h1>
                  <div class="section-title-notin mb--30">
                    <h2 class="heading-title" :style="subtitleStyle">
                      {{ translate('paginaNoEncontrada') }}
                    </h2>
                    <p :style="descriptionStyle">
                      {{ translate('errorDescription') }}
                    </p>
                  </div>
                  <div class="error-button-action">
                    <v-btn
                      size="x-large"
                      class="rn-btn btn-premium-notin"
                      :style="buttonStyle"
                      @click="handleBackToHome"
                    >
                      Volver al Inicio
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-main>

      <!-- Dynamic Footer con carga segura -->
      <template v-if="hasNotaria && NOTARIA.Theme">
        <FooterTwo v-if="NOTARIA.Theme.Footer.Design === 1" />
        <FooterOne v-else />
      </template>

      <!-- Support elements -->
      <ClientOnly>
        <div id="error-support-elements">
          <ElementsButtonUp />
          <ContactSocialChat v-if="hasNotaria && NOTARIA.SocialChat?.Show" />
          <ChatAIChatbot />
        </div>
      </ClientOnly>
    </div>
  </v-app>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from "~/store/index";
import { useStyles } from "~/store/styles";
import { storeToRefs } from "pinia";
import { useUtils } from "#imports";

// Importaciones manuales para asegurar compatibilidad en página de error
import HeaderNotinOne from "~/components/header/HeaderNotinOne.vue";
import FooterOne from "~/components/footer/FooterNotinOne.vue";
import FooterTwo from "~/components/footer/FooterNotinTwo.vue";
import StickyBar from "~/components/StickyBar.vue";

const props = defineProps({
  error: Object
})

const store = useStore()
const { NOTARIA } = storeToRefs(store)
const styles = useStyles()
const { $t, imagenIDtoSRC } = useUtils()

const hasNotaria = computed(() => !!NOTARIA.value)

const handleBackToHome = () => {
  window.location.href = '/';
}

const translate = (key: string) => {
  try {
    const res = $t(key);
    if (res && res !== key) return res;
    return key === 'paginaNoEncontrada' ? 'Página no encontrada' : 'Lo sentimos, la página que buscas no existe.';
  } catch (e) {
    return key === 'paginaNoEncontrada' ? 'Página no encontrada' : 'Lo sentimos, la página que buscas no existe.';
  }
}

// Estilos de Identidad Visual
const pageBackground = computed(() => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: NOTARIA.value?.Theme?.General?.FondoCss?.BackgroundColor || '#f8f9fa',
  backgroundImage: NOTARIA.value?.Theme?.Home?.Banner?.Imagen ? `linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)), url(${imagenIDtoSRC(NOTARIA.value.Theme.Home.Banner.Imagen)})` : '',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed'
}))

const codeStyle = computed(() => ({
  color: NOTARIA.value?.Theme?.General?.TituloCss?.Color || '#c99c33',
  fontFamily: NOTARIA.value?.Theme?.General?.TituloCss?.Font || 'inherit'
}))

const subtitleStyle = computed(() => ({
  color: NOTARIA.value?.Theme?.General?.TituloCss?.Color || '#333',
  fontFamily: NOTARIA.value?.Theme?.General?.TituloCss?.Font || 'inherit',
  fontSize: '2.8rem'
}))

const descriptionStyle = computed(() => ({
  color: NOTARIA.value?.Theme?.General?.TextoCss?.Color || '#666',
  fontFamily: NOTARIA.value?.Theme?.General?.TextoCss?.Font || 'inherit',
  fontSize: '1.25rem',
  lineHeight: '1.6'
}))

const buttonStyle = computed(() => ({
  backgroundColor: NOTARIA.value?.Theme?.General?.BotonCss?.BackgroundColor || '#c99c33',
  color: NOTARIA.value?.Theme?.General?.BotonCss?.Color || '#ffffff',
  borderColor: NOTARIA.value?.Theme?.General?.BotonCss?.BackgroundColor || '#c99c33'
}))
</script>

<style scoped lang="scss">
.error-page-wrapper {
  width: 100%;
}

.error-main-content {
  flex: 1;
  padding: 100px 0;
}

.error-glass-container {
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 80px 60px;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 40px 100px -20px rgba(0, 0, 0, 0.15);
  display: inline-block;
  max-width: 850px;
  width: 95%;
}

.error-code-identity {
  font-size: clamp(8rem, 25vw, 14rem);
  font-weight: 900;
  margin: 0;
  opacity: 0.12;
  line-height: 0.7;
  letter-spacing: -8px;
}

.btn-premium-notin {
  padding: 0 50px !important;
  height: 64px !important;
  font-size: 1.2rem !important;
  font-weight: 600 !important;
  border-radius: 12px !important;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
  box-shadow: 0 10px 25px rgba(0,0,0,0.12) !important;
  text-transform: none !important;
  
  &:hover {
    transform: translateY(-5px) scale(1.02) !important;
    box-shadow: 0 20px 40px rgba(0,0,0,0.18) !important;
  }
}

@media (max-width: 600px) {
  .error-glass-container {
    padding: 50px 25px;
  }
  .error-code-identity {
    font-size: 7rem;
    letter-spacing: -3px;
  }
  .heading-title {
    font-size: 2rem !important;
  }
}
</style>
